import {
  memo,
  MouseEvent,
  ReactElement,
  useCallback,
  useMemo,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useSelector } from 'react-redux';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import { EIconSize } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { getMountPath } from 'utils/generic/route-manipulation';
import { isMobile } from 'utils/is-mobile';
import { ENotRealSports } from 'interfaces/not-real-sports';
import { ELayoutTypes, EPageTypes, TGamesGroups } from 'interfaces/sportsbook';
import { ESportsbookPageTypes } from 'interfaces/sportsbook-configs';
import { INFINITE_SCROLL_ID } from 'newelements/Sportsbook/layouts/classic/DesktopSportsbookClassic';
import { EventCollapse } from 'components/EventCollapse';
import { GlobalIcon } from 'components/GlobalIcon';
import { MatchCardInline } from 'components/MatchCardInline';
import { MatchCardInlineUSA } from 'components/Shared/MatchCardInlineUSA';
import { NoItem } from 'components/Shared/NoItem';
import { UrlWrapper } from 'components/Shared/UrlWrapper';
import { CompetitionSkeleton } from 'components/UI/Skeletons/CompetitionSkeleton';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useScrollToTop } from 'hooks/useScrollToTop';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { getMarketsCount } from 'store/selectors/new-sportsbook';
import './index.less';

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

type TProps = {
  layoutType: ELayoutTypes;
  pageType: EPageTypes;
  groups: TGamesGroups | null;
  handleMatchClick: (event: MouseEvent, id: number) => void;
  isAllCollapsed?: boolean;
};

const INITIAL_COUNT = 15;
const LOAD_MORE_COUNT = 4;
export const ORIGINAL_MARKET_COUNT = 17;

export const SportsbookGameList = memo(
  ({
    layoutType,
    pageType,
    groups,
    handleMatchClick,
    isAllCollapsed
  }: TProps): ReactElement => {
    const configs = useSportsbookConfigs();

    const [groupsCount, setGroupsCount] = useState(INITIAL_COUNT);

    const { t } = useTranslation();
    const { path, params } = useSbRouteMatch();

    const isLive = pageType === EPageTypes.live;
    const isPrematch = pageType === EPageTypes.prematch;

    const getterParam = `${
      isPrematch ? params.competition : params.sport
    }_${pageType}`;

    const marketsCount = useSelector(getMarketsCount)[getterParam] || null;

    const isFavoriteTeam = params.category === ENotRealSports.team;
    const activeGameId = isFavoriteTeam ? params.competition : '';
    const isMobileTodayEvents =
      isMobile() && params.category === ENotRealSports.todayEvents;

    const scrollTarget = useMemo(() => {
      if (isMobile()) {
        return '';
      } else {
        if (configs.pageType === ESportsbookPageTypes.prematch) {
          return INFINITE_SCROLL_ID;
        } else {
          return 'prematch-left-sidebar-wrapper';
        }
      }
    }, []);

    useScrollToTop(
      scrollTarget,
      isLive ? [params.sport] : [params.sport, params.competition]
    );

    const data = useMemo(() => {
      if (!groups) {
        return null;
      }

      if (isAllCollapsed || configs.fixedScroll) {
        return groups;
      }

      return groups.slice(0, groupsCount);
    }, [groups, groupsCount, isAllCollapsed]);

    const isMoreDataAvailable = useMemo(() => {
      if (!groups || !data) {
        return false;
      }

      return data.length < groups.length;
    }, [data, groups]);

    const loadMore = useCallback(() => {
      setGroupsCount(prev => prev + LOAD_MORE_COUNT);
    }, [groups]);

    if (!data) {
      return (
        <CompetitionSkeleton
          category={params.category}
          sportAlias={params.sport}
          includeCollapse
          hideMargin
        />
      );
    }

    if (!data.length) {
      return (
        <NoItem
          title={t('sportsbook.noGame')}
          text={t('sportsbook.noGameText')}
          size="medium"
        />
      );
    }

    return (
      <InfiniteScroll
        scrollableTarget={scrollTarget}
        dataLength={data.length}
        next={loadMore}
        hasMore={isMoreDataAvailable}
        scrollThreshold={0.6}
        loader={null}
      >
        {data.map(({ name, details, games }) => (
          <EventCollapse
            key={name}
            title={
              <div className="sportsbook-game-list-group-title">
                {isLive && (
                  <GlobalIcon
                    lib="flags"
                    name={details.region.alias}
                    size={EIconSize._24}
                    className="flagIcon"
                  />
                )}
                <span className="sportsbook-game__name">{name}</span>
              </div>
            }
            defaultActive={!isAllCollapsed}
          >
            <div className="gamesWrapper">
              {(isMobileTodayEvents
                ? sortByOrder(games, 'start_ts')
                : games
              ).map(game => {
                const originalMarketsCount = SpringConfigs.MOCKED_DATA
                  ? ORIGINAL_MARKET_COUNT
                  : marketsCount?.[game.id];

                return (
                  <UrlWrapper
                    dataTestId="game"
                    key={game.id}
                    url={`${getMountPath(path)}/${details.sport.alias}/${
                      details.region.alias
                    }/${details.competition.id}/${game.id}`}
                  >
                    {layoutType === ELayoutTypes.usa ? (
                      <MatchCardInlineUSA
                        game={{
                          ...game,
                          markets_count: originalMarketsCount
                        }}
                        competitionName={details.competition.name}
                        sportAlias={details.sport.alias}
                        comp={{ id: details.competition.id }}
                        externalRedirect={e =>
                          handleMatchClick(e as MouseEvent, game.id)
                        }
                        withMoreWagesWrapper={true}
                        regionAlias={details.region.alias}
                        showBBIcon
                        showFavoriteButton
                        isLive={isLive}
                        detailedTeamsInfo
                      />
                    ) : (
                      <MatchCardInline
                        sportId={details.sport.id}
                        game={{
                          ...game,
                          markets_count: originalMarketsCount
                        }}
                        currentSport={details.sport.alias}
                        regionName={details.region.name}
                        sportName={details.sport.name}
                        competitionName={details.competition.name}
                        onClick={e =>
                          handleMatchClick(e as MouseEvent, game.id)
                        }
                        competitionId={details.competition.id}
                        isLive={isLive}
                        isActive={activeGameId === String(game.id)}
                      />
                    )}
                  </UrlWrapper>
                );
              })}
            </div>
          </EventCollapse>
        ))}
      </InfiniteScroll>
    );
  }
);
